import * as React from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { EditSmallIcon, InfoIcon, SteamIcon, SuccessIcon } from '@icons';
import gtmTracker from '@utils/gtmTracker';

import { useModals } from '@shared/contexts';


import { useMutationAttachTradeLink } from '@hooks/api/user/use-mutation-attach-trade-link.ts';
import { useQueryGetMe } from '@hooks/api/user/use-query-me.ts';

import { Button } from '@components/button';
import { Input } from '@components/input';
import { ModalName } from '@components/modals';

import * as Styled from './trade-link.styled.ts';

const TradeLink = () => {
	const { t } = useTranslation(['main', 'notifications', 'profile', 'welcome']);
	const { openModal } = useModals();
	const { mutate: attachTradeLink, isError, isSuccess } = useMutationAttachTradeLink();

	const { data: user } = useQueryGetMe();

	const handleTradeClick = () => {
		openModal(ModalName.TRADE_LINK_HELP);
	};

	const [link, setLink] = React.useState('');
	const [isValid, setIsValid] = React.useState(true);

	const [editTrade, setEditTrade] = React.useState(false);

	const handleChange = (e: any) => {
		const value = e.target.value;
		setLink(value);

		const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
		setIsValid(urlRegex.test(value));
	};

	const handleEditTrade = () => {
		gtmTracker.trackEvent('click', {
			event_category: 'User_profile',
			event_action: 'Click',
			event_label: 'Trade link edit'
		});
		setEditTrade(true);
	};

	const cancelEdit = () => {
		setEditTrade(false);
		setIsValid(true);
		setLink(user?.steam_trade_url || '');
	};

	const Enter = () => {
		if (!isValid) {
			toast.error(t('notifications:wrong_trade_link'), {
				hideProgressBar: true
			});

			return;
		}

		attachTradeLink({ steam_trade_url: link });
	};

	React.useEffect(() => {
		if (isError) {
			setIsValid(false);
			toast.error(t('notifications:wrong_trade_link'), {
				hideProgressBar: true
			});
		}

		if (isSuccess) {
			toast.success(t('notifications:trade_link_attached'), {
				hideProgressBar: true
			});

			setEditTrade(false);
		}
	}, [isError, isSuccess, t]);

	React.useEffect(() => {
		setLink(user?.steam_trade_url || '');
	}, [user?.steam_trade_url]);

	return (
		<Styled.Wrapper>
			<Styled.Title>
				<Styled.TitleInfo>
					<SteamIcon />
					Trade Link
				</Styled.TitleInfo>
				{!editTrade && (
					<Button color='dark' size='large' onClick={handleEditTrade}>
						<EditSmallIcon />
					</Button>
				)}
			</Styled.Title>

			{user?.steam_trade_url ? (
				<Styled.Success>
					{editTrade ? (
						<Styled.Text>
							<InfoIcon />
							{t('attach_trade_link')}
						</Styled.Text>
					) : (
						<Styled.Attached>
							<SuccessIcon />
							{t('profile:steam_attached')}
						</Styled.Attached>
					)}
					{editTrade ? (
						<Styled.InputWrapper>
							<Styled.Subtitle>{t('link')}</Styled.Subtitle>
							<Input
								type='text'
								disabled={!editTrade}
								placeholder='https://'
								value={link || ''}
								onChange={handleChange}
							/>
						</Styled.InputWrapper>
					) : (
						<Styled.LinkWrapper>
							<div>{t('link')}</div>
							<Styled.LinkStyled>{user?.steam_trade_url}</Styled.LinkStyled>
						</Styled.LinkWrapper>
					)}
					{editTrade && (
						<Styled.Buttons>
							<Button onClick={Enter} color='light'>
								{t('attach')}
							</Button>
							<Button onClick={cancelEdit} color='dark'>
								{t('cancel')}
							</Button>
						</Styled.Buttons>
					)}
				</Styled.Success>
			) : (
				<Styled.Main>
					<Styled.Text>
						<InfoIcon />
						{t('attach_trade_link')}
					</Styled.Text>
					<Styled.InputWrapper>
						<Styled.Subtitle>{t('link')}</Styled.Subtitle>
						<Input
							type='text'
							disabled={!editTrade}
							placeholder='https://'
							value={link || ''}
							onChange={handleChange}
						/>
					</Styled.InputWrapper>
					{editTrade ? (
						<Styled.Buttons>
							<Button onClick={Enter} color='light'>
								{t('attach')}
							</Button>
							<Button onClick={cancelEdit} color='dark'>
								{t('cancel')}
							</Button>
						</Styled.Buttons>
					) : (
						<Styled.Instructions onClick={handleTradeClick}>{t('welcome:trade_link_text2')}</Styled.Instructions>
					)}
				</Styled.Main>
			)}
		</Styled.Wrapper>
	);
};

export default TradeLink;
